import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '@/api/client';
import { useSession } from '@/auth/SessionContext';
import { ScreenBackButton } from '@/components/ScreenBackButton';
import { pwaPathFromDeepLink } from '@/lib/deepLink';
import { formatMessage } from '@/lib/formatMessage';
import { PWA_ALERTS_BADGE_REFRESH_EVENT } from '@/hooks/useAlertsBadgeCount';
import { DepositCountdown } from '@/components/DepositCountdown';

type Notif = {
  id: string;
  type: string;
  title: string;
  body?: string | null;
  status: string;
  createdAt: string;
  deepLinkPath?: string | null;
  dismissedAt?: string | null;
  backgroundColor?: string;
  payload?: {
    conversationId?: string;
    resourceId?: string;
    startDate?: string;
    endDate?: string;
    depositExpiresAt?: string;
  } | null;
};

type MessageAuthorizationItem = {
  id: string;
  type: string;
  status: string;
  draftContent: string;
  bookingId: string | null;
  virtualAgentId: string | null;
  createdAt: string;
  booking?: {
    id: string;
    customerName: string;
    status: string;
    publicReference: string | null;
    resource?: { name: string } | null;
  } | null;
};

function authorizationTypeLabel(type: string): string {
  if (type === 'booking_confirmation') return 'Confirmación de reserva';
  if (type === 'outbound_agent') return 'Mensaje al visitante';
  return type;
}

function notificationsQuery(take: number, showHistory: boolean): string {
  const p = new URLSearchParams({ take: String(take) });
  if (showHistory) {
    p.set('includeDismissed', 'true');
    p.set('includeResolved', 'true');
  } else {
    p.set('includeDismissed', 'false');
  }
  return `/api/console/notifications?${p.toString()}`;
}

function reservationPrefill(n: Notif): {
  checkIn?: string;
  checkOut?: string;
  resourceId?: string;
} | null {
  const payload = n.payload;
  if (!payload) return null;
  const checkIn = payload.startDate?.slice(0, 10);
  const checkOut = payload.endDate?.slice(0, 10);
  const resourceId = payload.resourceId?.trim();
  if (!checkIn && !checkOut && !resourceId) return null;
  return {
    ...(checkIn ? { checkIn } : {}),
    ...(checkOut ? { checkOut } : {}),
    ...(resourceId ? { resourceId } : {}),
  };
}

export function NotificationsPage() {
  const { get, post, can } = useSession();
  const navigate = useNavigate();
  const [items, setItems] = useState<Notif[]>([]);
  const [total, setTotal] = useState(0);
  const [authorizations, setAuthorizations] = useState<MessageAuthorizationItem[]>([]);
  const [authTotal, setAuthTotal] = useState(0);
  const [authErr, setAuthErr] = useState<string | null>(null);
  const [authBusyId, setAuthBusyId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [declineBusyId, setDeclineBusyId] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const canReadAuthorizations = can('pwa.message_authorizations.read');
  const canWriteAuthorizations = can('pwa.message_authorizations.write');

  const loadAuthorizations = useCallback(async () => {
    if (!canReadAuthorizations) return;
    try {
      const data = await get<{ items: MessageAuthorizationItem[]; total: number }>(
        '/api/console/message-authorizations?status=pending&take=20'
      );
      setAuthorizations(data.items);
      setAuthTotal(data.total);
      setAuthErr(null);
    } catch (e) {
      setAuthErr(e instanceof ApiError ? e.message : 'No se pudieron cargar autorizaciones');
    }
  }, [get, canReadAuthorizations]);

  const load = useCallback(async () => {
    try {
      const data = await get<{ items: Notif[]; total: number }>(notificationsQuery(40, showHistory));
      setItems(data.items);
      setTotal(data.total);
      setErr(null);
      window.dispatchEvent(new Event(PWA_ALERTS_BADGE_REFRESH_EVENT));
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Error');
    }
    await loadAuthorizations();
  }, [get, showHistory, loadAuthorizations]);

  useEffect(() => {
    const boot = window.setTimeout(() => void load(), 0);
    const id = window.setInterval(() => void load(), 12_000);
    return () => {
      clearTimeout(boot);
      clearInterval(id);
    };
  }, [load]);

  return (
    <div>
      <div className="pwa-screen-head pwa-screen-head--notifications">
        <ScreenBackButton to="/" />
        <h1 className="pwa-screen-head__title">Alertas</h1>
        <label className="pwa-toggle">
          <span className="pwa-toggle__label">Historial</span>
          <span className="pwa-toggle__switch">
            <input
              type="checkbox"
              role="switch"
              className="pwa-toggle__input"
              checked={showHistory}
              onChange={(e) => setShowHistory(e.target.checked)}
              aria-describedby="notifications-mode-hint"
            />
            <span className="pwa-toggle__track" aria-hidden />
          </span>
        </label>
      </div>
      <p id="notifications-mode-hint" className="pwa-muted">
        {showHistory ?
          `Incluye resueltas y descartadas · ${total} en esta vista.`
        : `Solo pendientes (sin descartar) · ${total} en esta vista.`}{' '}
        Actualización cada ~12 s.
      </p>
      {err ? <p className="pwa-err">{err}</p> : null}
      {authErr ? <p className="pwa-err">{authErr}</p> : null}

      {canReadAuthorizations && authorizations.length > 0 ?
        <section style={{ marginTop: '1rem' }}>
          <h2 className="pwa-screen-head__title" style={{ fontSize: '1.05rem', marginBottom: '0.5rem' }}>
            Autorizaciones de mensajes ({authTotal})
          </h2>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {authorizations.map((a) => {
              const bookingLabel =
                a.booking ?
                  `${a.booking.customerName}${a.booking.resource?.name ? ` · ${a.booking.resource.name}` : ''}`
                : null;
              return (
                <li key={a.id} className="pwa-card" style={{ boxShadow: 'none', marginBottom: '0.75rem' }}>
                  <div style={{ fontWeight: 600 }}>{authorizationTypeLabel(a.type)}</div>
                  {bookingLabel ?
                    <div className="pwa-muted" style={{ marginTop: 4 }}>
                      {bookingLabel}
                    </div>
                  : null}
                  <div className="pwa-alert-body chat-msg-format" style={{ marginTop: 8 }}>
                    {formatMessage(a.draftContent)}
                  </div>
                  <div className="pwa-muted" style={{ fontSize: '0.75rem', marginTop: 8 }}>
                    {new Date(a.createdAt).toLocaleString()}
                  </div>
                  {canWriteAuthorizations ?
                    <div style={{ marginTop: 12, display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="pwa-btn pwa-btn--primary pwa-btn--sm"
                        disabled={authBusyId === a.id}
                        onClick={() => {
                          setAuthBusyId(a.id);
                          void post(`/api/console/message-authorizations/${a.id}/approve`, {})
                            .then(() => load())
                            .catch((e) => {
                              setAuthErr(e instanceof ApiError ? e.message : 'No se pudo aprobar');
                            })
                            .finally(() => setAuthBusyId(null));
                        }}
                      >
                        {authBusyId === a.id ? 'Aprobando…' : 'Aprobar y enviar'}
                      </button>
                      <button
                        type="button"
                        className="pwa-btn pwa-btn--secondary pwa-btn--sm"
                        disabled={authBusyId === a.id}
                        onClick={() => {
                          if (!window.confirm('¿Rechazar esta autorización de mensaje?')) return;
                          setAuthBusyId(a.id);
                          void post(`/api/console/message-authorizations/${a.id}/reject`, {})
                            .then(() => load())
                            .catch((e) => {
                              setAuthErr(e instanceof ApiError ? e.message : 'No se pudo rechazar');
                            })
                            .finally(() => setAuthBusyId(null));
                        }}
                      >
                        Rechazar
                      </button>
                    </div>
                  : null}
                </li>
              );
            })}
          </ul>
        </section>
      : null}

      <ul style={{ listStyle: 'none', padding: 0, margin: '1rem 0 0' }}>
        {items.map((n) => {
          const linkTo = pwaPathFromDeepLink(n.deepLinkPath);
          const conversationId =
            typeof n.payload?.conversationId === 'string' ? n.payload.conversationId.trim() : '';
          const chatTo = conversationId
            ? `/conversations/${conversationId}`
            : linkTo?.startsWith('/conversations/')
              ? linkTo
              : null;
          const showReserve =
            n.type === 'calendar.booking_visitor_confirmed' &&
            n.status === 'open' &&
            !n.dismissedAt &&
            Boolean(conversationId) &&
            can('pwa.calendar.write');
          return (
            <li
              key={n.id}
              className={n.backgroundColor ? 'pwa-alert-card' : 'pwa-card'}
              style={
                n.backgroundColor
                  ? { boxShadow: 'none', backgroundColor: n.backgroundColor }
                  : { boxShadow: 'none' }
              }
            >
              <div style={{ fontWeight: 600 }}>{n.title}</div>
              {n.body ?
                <div className="pwa-alert-body chat-msg-format" style={{ marginTop: 6 }}>
                  {formatMessage(n.body)}
                </div>
              : null}
              {n.type === 'calendar.booking_visitor_confirmed' && n.status === 'open' ?
                <DepositCountdown expiresAt={n.payload?.depositExpiresAt} />
              : null}
              <div className="pwa-muted" style={{ fontSize: '0.75rem', marginTop: 8 }}>
                {n.type} · {n.status}
                {n.dismissedAt ? ' · descartada' : ''} · {new Date(n.createdAt).toLocaleString()}
              </div>
              <div style={{ marginTop: 12, display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                {showReserve ?
                  <button
                    type="button"
                    className="pwa-btn pwa-btn--primary pwa-btn--sm"
                    onClick={() =>
                      navigate(`/conversations/${conversationId}`, {
                        state: { openReservation: reservationPrefill(n) ?? {} },
                      })
                    }
                  >
                    Reservar
                  </button>
                : null}
                {n.type === 'calendar.booking_visitor_confirmed' &&
                n.status === 'open' &&
                !n.dismissedAt &&
                can('pwa.notifications.write') ?
                  <button
                    type="button"
                    className="pwa-btn pwa-btn--secondary pwa-btn--sm"
                    disabled={declineBusyId === n.id}
                    onClick={() => {
                      if (!window.confirm('¿No reservar? Se libera la seña pendiente y el chat vuelve a automático.')) {
                        return;
                      }
                      setDeclineBusyId(n.id);
                      setErr(null);
                      void post(`/api/console/notifications/${n.id}/decline-booking`, {})
                        .then(() => {
                          window.dispatchEvent(new Event(PWA_ALERTS_BADGE_REFRESH_EVENT));
                          return load();
                        })
                        .catch((e) => {
                          setErr(e instanceof ApiError ? e.message : 'No se pudo liberar la reserva');
                        })
                        .finally(() => setDeclineBusyId(null));
                    }}
                  >
                    {declineBusyId === n.id ? 'Liberando…' : 'No reservar'}
                  </button>
                : null}
                {chatTo ?
                  <Link to={chatTo} className="pwa-btn pwa-btn--secondary pwa-btn--sm">
                    Ver chat
                  </Link>
                : null}
                {n.dismissedAt && n.status === 'open' && can('pwa.notifications.write') ?
                  <button
                    type="button"
                    className="pwa-btn pwa-btn--secondary pwa-btn--sm"
                    onClick={() =>
                      void post(`/api/console/notifications/${n.id}/undismiss`, {}).then(() => {
                        window.dispatchEvent(new Event(PWA_ALERTS_BADGE_REFRESH_EVENT));
                        return load();
                      })
                    }
                  >
                    Quitar descarte
                  </button>
                : null}
                {n.status === 'open' && !n.dismissedAt && can('pwa.notifications.write') ?
                  <>
                    <button
                      type="button"
                      className="pwa-btn pwa-btn--secondary pwa-btn--sm"
                      onClick={() =>
                        void post(`/api/console/notifications/${n.id}/dismiss`, {}).then(() => {
                          window.dispatchEvent(new Event(PWA_ALERTS_BADGE_REFRESH_EVENT));
                          return load();
                        })
                      }
                    >
                      Descartar
                    </button>
                    <button
                      type="button"
                      className="pwa-btn pwa-btn--secondary pwa-btn--sm"
                      onClick={() =>
                        void post(`/api/console/notifications/${n.id}/resolve`, {}).then(() => {
                          window.dispatchEvent(new Event(PWA_ALERTS_BADGE_REFRESH_EVENT));
                          return load();
                        })
                      }
                    >
                      Resolver
                    </button>
                  </>
                : null}
                {n.status !== 'open' && can('pwa.notifications.write') ?
                  <button
                    type="button"
                    className="pwa-btn pwa-btn--secondary pwa-btn--sm"
                    onClick={() =>
                      void post(`/api/console/notifications/${n.id}/reopen`, {}).then(() => {
                        window.dispatchEvent(new Event(PWA_ALERTS_BADGE_REFRESH_EVENT));
                        return load();
                      })
                    }
                  >
                    Reabrir
                  </button>
                : null}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

